import { createContext } from "react";
import type { Appearance, Prompt } from "../data";
import type { ColumnSize } from "../Grid/Column";

const emptyFunc = () => undefined;
export type PromptLibraryContextProps = {
  /**
   * The appearance of the prompt card. Supported types are 'web', 'in-app', and 'm365'. If not provided, 'in-app' will be used.
   */
  appearance: Appearance;

  /**
   * The column size for each prompt card. The total screen size is 12, so 6 would mean two cards per row.
   */
  columnSize: ColumnSize;

  /**
   * Optional toggle to enable features related to user favoring a prompt
   */
  isFavoriteEnabled?: boolean;

  /**
   * Optional toggle to enable features related to user saving a prompt
   */
  isPromptSavingEnabled?: boolean;

  /**
   * Optional toggle to enable features related to user editing a prompt
   */
  isPromptEditingEnabled?: boolean;

  /**
   * Optional toggle to enable features related to user sharing a prompt
   */
  isPromptSharingEnabled?: boolean;

  /**
   * Optional toggle to enable features related to user sharing a prompt via email
   */
  isPromptSharingViaEmailEnabled?: boolean;

  /**
   * Optional toggle to enable prompt ring tag
   */
  isPromptRingTagEnabled?: boolean;

  /**
   * Optional toggle to enable features related to workgroup tenant publishing.
   */
  isPromptSharingToWorkgroupEnabled?: boolean;

  /**
   * Optional toggle to enable features related to workgroup tenant publishing.
   */
  isPromptRemovingFromWorkgroupEnabled?: boolean;

  /**
   * Optional toggle to enable workgroup tab
   */
  isWorkgroupTabEnabled?: boolean;

  /**
   * Optional toggle reflecting if declarative copilot is selected
   */
  isDeclarativeCopilotSelected?: boolean;

  /**
   * Optional toggle denoting if prompt library is shown with Web toggle enabled
   */
  isWebChat?: boolean;

  /**
   * Callback function that is invoked when a prompt is clicked.
   * @param prompt prompt that's clicked
   * @param displayPosition position of the prompt clicked relative to the dialog
   * @returns
   */
  onPromptClicked: (prompt: Prompt, displayPosition?: number) => void;

  /**
   * Callback function that is invoked when a prompt is favorited/unfavorited by an user.
   * @param prompt prompt that's favorited/unfavorited
   * @param favorited true if the prompt is favorited, false otherwise
   * @returns
   */
  onFavoriteToggled: (prompt: Prompt, favorited: boolean) => void;

  /**
   * Callback function that is invoked when a prompt is edited by a user.
   * @param prompt prompt that's being edited
   * @returns
   */
  onEditToggled?: (prompt: Prompt) => void;

  /**
   * Callback function that is invoked when an edit is saved by a user.
   * @param prompt prompt that's deleted
   * @returns
   */
  onEditSaved?: (prompt: Prompt) => void;
};

export const PromptLibraryContext = createContext<PromptLibraryContextProps>({
  appearance: "in-app",
  columnSize: 4,
  onPromptClicked: emptyFunc,
  onFavoriteToggled: emptyFunc,
});
